import React, { useMemo, useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import { designSystem } from '@yola/ws-ui';
import DesignOtherStylesContext from '../helpers/design-other-styles-context';

const { SliderPattern } = designSystem;

const isIndeterminate = (propsValues) =>
  propsValues.some((value, index, values) => value !== values[0]);

function SliderOption({
  id,
  options,
  title,
  popover,
  iconGlyph,
  mixedLabel,
  step,
  min,
  max,
  normalizeValue,
  transformValue,
  computeFallbackValue,
}) {
  const { state, onOptionChange } = useContext(DesignOtherStylesContext);

  const popoverProps = useMemo(
    () => ({
      ...popover,
      content: popover.renderContent(),
    }),
    [popover]
  );

  const properties = useMemo(() => options.map((opt) => opt.property), [options]);

  const values = useMemo(
    () =>
      properties.map((property) => {
        const value = state[property];

        return value ? normalizeValue(value) : computeFallbackValue();
      }),
    [state, properties, normalizeValue, computeFallbackValue]
  );

  const items = useMemo(
    () =>
      options.map((opt, index) => ({
        value: values[index] || min,
        iconGlyph: opt.iconGlyph,
        onChange: (newValue) => onOptionChange(id, transformValue(newValue), [opt.property]),
      })) || null,
    [options, min, values, transformValue, id, onOptionChange]
  );

  const indeterminate = isIndeterminate(values);
  const currentValue = Number(indeterminate ? Math.max(...values) : values[0]);
  const value = currentValue || min;

  const onSliderChange = useCallback(
    (newValue) => {
      onOptionChange(id, transformValue(newValue), properties);
    },
    [properties, onOptionChange, id, transformValue]
  );

  return (
    <SliderPattern
      value={value}
      title={title}
      items={items}
      iconGlyph={iconGlyph}
      mixedLabel={mixedLabel}
      step={step}
      min={min}
      max={max}
      indeterminate={indeterminate}
      popoverProps={popoverProps}
      onChange={onSliderChange}
    />
  );
}

SliderOption.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      iconGlyph: PropTypes.string,
      property: PropTypes.string.isRequired,
    })
  ).isRequired,
  iconGlyph: PropTypes.string.isRequired,
  mixedLabel: PropTypes.string.isRequired,
  step: PropTypes.number.isRequired,
  min: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  popover: PropTypes.object.isRequired,
  normalizeValue: PropTypes.func.isRequired,
  transformValue: PropTypes.func.isRequired,
  computeFallbackValue: PropTypes.func.isRequired,
};

export default SliderOption;
